import type { CSSProperties } from 'react';
import { ProgressBadge } from './ProgressBadge';
import { usePhaseProgress } from './phaseProgress';
import { useLanguage } from '../../contexts/LanguageContext';

export interface PhaseNavItem {
  // Identifies the tab (matches the section it reveals).
  id: string;
  label: string;
}

// HOW MANY PILLS TO A ROW AT MOST, and the rows are evened out from it rather than filled
// greedily: eight phases are 4 and 4, not 5 and 3, and seven are 4 and 3. A last row
// holding one pill under a full one reads as an afterthought — as though the eighth phase
// were a different kind of thing from the seven above it, which is exactly what it is not.
const MAX_PER_ROW = 4;

// The Reader's phase navigation and its progress, one pill per phase of the pipeline on
// screen. A pill carries everything about its phase: its number, how far along it is, and
// its name. Clicking one opens that phase; exactly one section shows at a time (see
// NoticeViewer), and this bar is the only way to switch.
//
// The one element that never unmounts, so the progress it reports survives switching
// phases exactly like the runs behind it. It is not sticky itself: it sits inside the
// viewer's sticky header — which is why its height is the thing it is judged on, being
// paid for on every screenful of the phase underneath rather than only on the first.
//
// HOW FAR ALONG IS THE RING, and it is the whole report. The number is drawn inside an arc
// of its own fraction: a finished phase is a closed ring rather than a filled disc, so
// every pill in the row is the same object at a different reading. There was a linear bar
// under each as well, saying the same number as a length — two drawings of one fraction,
// and the taller of the two.
//
// DRAWN THE MOMENT IT IS KNOWN. The rings used to be revealed one phase at a time, left to
// right, 380 ms apart, and only once the PDF had paginated — five to ten seconds of a bar
// filling in front of somebody who had reloaded a document that was already finished.
//
// That sweep was written for the linear bars this row used to carry. The stores rehydrate
// from localStorage synchronously, so every finished phase painted full on the first frame
// with no transition, while Phase 1 sat empty waiting for the PDF and then filled on its
// own — the row read as though it were running backwards. Replaying it in order fixed that.
//
// The bars are gone (see above) and so is the problem: a ring going straight to full has
// none of the weight of a bar sweeping across a card, and there is nothing left for Phase 1
// to fill visibly late. What is left is a wait, and a document whose work is done should
// say so on the frame it opens.
//
// A phase that is RUNNING hatches its number with moving stripes. One the running chain
// has not reached YET wears the same hatch, held still. Queued and working are the same
// pipeline in two moments, so they are the same mark in two states — rather than a third
// colour that has to be learnt — and the difference is the one the eye reads first:
// movement.
export function PhaseNav({
  items,
  active,
  onSelect,
  docId,
  docLoaded,
  incomingPhases,
}: {
  // One per phase, in order.
  items: PhaseNavItem[];
  active: string;
  onSelect: (id: string) => void;
  docId: string;
  // Phase 1: the PDF is open and paginated.
  docLoaded: boolean;
  // The phases a run in flight will reach, or null when no run is moving. Each of them that
  // is neither done nor busy is coming. A SET, not "up to N": a run can pass a phase it never
  // runs (the materials), and hatching that one would promise work nobody is doing. Null
  // while a run is PAUSED as well as while there is none: a held run is not on its way, and
  // saying it is would be the one lie this mark could tell.
  incomingPhases?: ReadonlySet<number> | null;
}) {
  const { t } = useLanguage();
  const { phases } = usePhaseProgress(docId, docLoaded);

  // WHAT A PHASE IS DOING: its measure, whether the opening sweep has reached it, and the
  // one class that says both on the badge.
  const stateOf = (i: number) => {
    const state = phases[i];
    const p = state ?? { n: i + 1, fraction: 0, busy: false, done: false };
    // The disc says what the phase is DOING: hatched while running, hatched and
    // still while queued, plain otherwise. Not what it has FINISHED — that is the
    // ring's job now, and a finished phase used to fill its disc solid green, so
    // finished and half-finished were two different objects in one row. A full
    // ring around a plain disc is the same object as a half ring around one.
    const grey = 'bg-slate-300 text-slate-600';
    const incoming = !!incomingPhases?.has(p.n) && !p.done && !p.busy;
    // RUNNING is the ring's to say now (a spinner — see ProgressBadge); the disc stays grey.
    const badge = incoming ? `phase-stripes-still ${grey}` : grey;
    return { p, badge };
  };
  // ONE ROW OF PILLS PER FOUR PHASES, and nothing else. There were two drawings of this bar
  // — cards with a progress bar apiece, and these pills — behind a fold that remembered
  // which you last used.
  //
  // The cards are gone and the pills are all there is. Two drawings of one bar is two
  // things to keep true of eight phases, and the fold was the tell: a bar worth folding on
  // every screenful is a bar that was too big for what it says. What the cards had that
  // these do not is the linear bar, and the ring around each number already carries that
  // number — the same fraction, drawn as an arc instead of a length, in a tenth of the
  // height. Losing it costs a reading of "how far along" at a glance across the row, and
  // buys back a third of the viewport on every phase under it.
  //
  // EVERY PILL THE SAME WIDTH: a column's share of the bar, set on each one. Sized to their
  // labels they came out seven different lengths, and a row of différently sized targets is a
  // row you aim at rather than scan: the eye reads the ragged right edge as information, and it
  // is not — it is how long the words happen to be.
  //
  // A WRAPPING ROW, CENTRED, and not a grid: a grid lays a short last row out from the left,
  // three pills under four with a hole at the end. Centred, the rows read as one block.
  //
  // TWO TO A ROW ON A PHONE. A quarter of a phone's width is 85 px, narrower than "Chargement" or
  // "Publication", and the names ran out of their pills. The columns are a CSS variable so the
  // breakpoint can set them: `--cols` is the wide count, `--c` the one in force.
  const rows = Math.max(1, Math.ceil(items.length / MAX_PER_ROW));
  const cols = Math.ceil(items.length / rows);

  return (
    <div className="py-2">
      <div
        style={{ '--cols': cols } as CSSProperties}
        className="mx-auto flex w-full max-w-5xl flex-wrap items-stretch justify-center gap-2 px-4 [--c:2] sm:[--c:var(--cols)]"
      >
        {/* One pill per ITEM, not per progress slot. The two lists are written apart — the
            phases in viewerPipelines, their measures in phaseProgress — and a pill drawn
            only where a measure exists is a pill that can go missing: a phase past the end
            of the slots was once dropped here entirely, so its tab could not be opened at
            all. A phase with no measure draws an empty ring and is still a phase you can
            open.

            Each pill carries its OWN white fill rather than sitting inside one shared bar
            background: the bar's own background was dropped (the blueprint page-glow shows
            through the gaps instead), so a pill with no fill of its own would be invisible
            against it — an outline with nothing behind it. */}
        {items.map((item, i) => {
          const { p, badge } = stateOf(i);
          const on = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={on ? 'page' : undefined}
              // A column's share of the row: the width less the gaps (gap-2, 0.5rem), over the columns.
              style={{ width: 'calc((100% - (var(--c) - 1) * 0.5rem) / var(--c))' }}
              className={`flex min-w-0 items-center justify-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-2.5 text-xs font-medium shadow-sm transition-colors ${
                on
                  // 2 px of blue: the 1 px border plus a 1 px ring outside it. A ring is a shadow and
                  // takes no room, where a 2 px border would grow the selected pill by a pixel a side
                  // and shift the row every time the selection moves.
                  ? 'border-blue-500 text-blue-700 ring-1 ring-blue-500'
                  : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              {/* The number, ringed with its own progress — see ProgressBadge. The ring is
                  the whole of what this bar reports now, so it is never allowed to shrink
                  with the column. */}
              <ProgressBadge fraction={p.fraction} busy={p.busy} badgeClass={badge}>
                {p.n}
              </ProgressBadge>
              {/* Full contrast on the closed pills, green on the open one — the name
                  carries the selection, not just the border. It wraps rather than
                  truncating: the columns are wide enough for every phase name at the
                  bar's own width, and a phase whose name is cut off is a phase you
                  cannot identify, which is the one thing a label must never be. */}
              <span
                className={`min-w-0 text-balance text-center leading-tight ${
                  on ? '' : 'text-slate-900'
                }`}
              >
                {item.label}
              </span>
              {/* The number is drawn as a disc and a screen reader cannot read it as one.
                  The name is beside it in the markup, so this does not repeat it. */}
              <span className="sr-only">
                {t('notice.phase')} {p.n}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
